import { Err, Ok, Result } from 'ts-res';
import { KameleoonData, BrowserType, BrowserDataType } from 'src/kameleoonData/types';
import {
  exhaustCheck,
  KameleoonError,
  KameleoonException,
} from 'src/kameleoonError';
import { EvaluationDataType, VersionMatchType } from 'src/targeting/types';
import { ConditionDataType, ICondition } from './types';
import { KameleoonLogger } from 'src/logging';

export class Browser implements ICondition {
  private browser?: BrowserType | null;
  private version?: string | null;
  private versionMatchType?: VersionMatchType | null;
  private error?: KameleoonError;

  constructor({ browser, version, versionMatchType }: ConditionDataType) {
    this.browser = browser;
    this.version = version;
    this.versionMatchType = versionMatchType;
  }

  public evaluate({
    targetingData,
  }: EvaluationDataType): Result<boolean, KameleoonError> {
    const browserData = targetingData?.[KameleoonData.Browser];

    if (browserData) {
      const result = this.checkCondition(browserData);

      if (this.error) {
        return Err(this.error);
      }

      return Ok(result);
    }

    return Ok(false);
  }

  private checkCondition(browserData: BrowserDataType): boolean {
    const { browser, version } = browserData;

    if (typeof this.browser !== 'string') {
      return false;
    }

    if (!this.version) {
      return browser === this.browser;
    }

    if (!this.versionMatchType || typeof version !== 'number') {
      return false;
    }

    const parsedVersion = parseFloat(this.version);

    if (isNaN(parsedVersion)) {
      KameleoonLogger.error`Failed to parse version: ${this.version} in Browser condition`;
      this.error = new KameleoonError(
        KameleoonException.VersionParse,
        this.version,
      );
    }

    switch (this.versionMatchType) {
      case VersionMatchType.EQUAL:
        return browser === this.browser && version === parsedVersion;
      case VersionMatchType.GREATER:
        return browser === this.browser && version > parsedVersion;
      case VersionMatchType.LOWER:
        return browser === this.browser && version < parsedVersion;
      default:
        KameleoonLogger.error`Unexpected comparing operation for Browser condition: ${this.versionMatchType}`;
        exhaustCheck(this.versionMatchType);
    }
  }
}
